import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { FileUp, Gavel, CalendarClock } from "lucide-react";
import { api, errMsg, fmtDate, HEARING_TYPES } from "@/lib/api";
import { Badge } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Section, F, btn } from "@/components/CaseBits";
import { SignedPdf } from "@/components/SignedPdf";
import { ProceedingRow } from "@/components/CaseForms";

const KINDS = [["procedural_order", "Procedural order"], ["interim_order", "Interim order"],
  ["interim_award", "Interim award"], ["final_award", "Final award"]];
const KIND_CLASS = { final_award: "bg-green-50 text-green-900", interim_award: "bg-emerald-50 text-emerald-900",
  interim_order: "bg-amber-50 text-amber-900", procedural_order: "bg-blue-50 text-blue-900" };

/** Arbitrator's composer: type the order, attach a signed PDF, fix the next date — one submission. */
const Composer = ({ caseId, onDone, prefill }) => {
  const blank = { kind: "procedural_order", number: "", title: "", issued_on: new Date().toISOString().slice(0, 10), body: "",
    next_at: "", hearing_type: "miscellaneous", purpose: "", mode: "physical", venue: "", advocate_change_id: null };
  const [f, setF] = useState(blank);
  useEffect(() => {
    if (prefill) setF((s) => ({ ...s, kind: prefill.kind, title: prefill.title, body: prefill.body, number: prefill.number || s.number,
      advocate_change_id: prefill.advocate_change_id || null, next_at: "" }));
  }, [prefill]);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const input = useRef(null);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const isAward = f.kind === "final_award";

  const submit = async (e) => {
    e.preventDefault();
    if (!f.body.trim() && !file) return toast.error("Type the order or attach its signed PDF");
    setBusy(true);
    try {
      const { data: order } = await api.post("/orders", { case_id: caseId, kind: f.kind, number: f.number || null,
        title: f.title, issued_on: f.issued_on || null, body: f.body || null,
        next_hearing_at: !isAward && f.next_at ? f.next_at : null, next_hearing_purpose: f.purpose || null,
        advocate_change_id: f.advocate_change_id });
      if (file) {
        const fd = new FormData();
        fd.append("kind", "order"); fd.append("target_id", order.id); fd.append("file", file);
        await api.post("/attachments", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      if (!isAward && f.next_at) {
        await api.post("/proceedings", { case_id: caseId, scheduled_at: f.next_at, hearing_type: f.hearing_type,
          purpose: f.purpose || `Next date after ${f.title}`, mode: f.mode, venue: f.venue || null });
      }
      toast.success(isAward ? "Final award recorded — proceedings concluded" : f.next_at ? "Order recorded and next date fixed" : "Order recorded");
      setF(blank); setFile(null); if (input.current) input.current.value = "";
      onDone();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="unified-order-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
        <div className="space-y-2">
          <Label className="label-caps">Kind</Label>
          <Select value={f.kind} onValueChange={(v) => set("kind", v)}>
            <SelectTrigger className="rounded-none h-10" data-testid="order-kind-select"><SelectValue /></SelectTrigger>
            <SelectContent>{KINDS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <F label="Number" value={f.number} onChange={(v) => set("number", v)} testId="order-number-input" placeholder="PO-3" />
        <F label="Title" required value={f.title} onChange={(v) => set("title", v)} testId="order-title-input" />
        <F label="Dated" type="date" value={f.issued_on} onChange={(v) => set("issued_on", v)} testId="order-date-input" />
      </div>
      <div className="space-y-2">
        <Label className="label-caps">Order text — a plain PDF (case no., cause title, tribunal, date, next date) is generated and saved to Documents</Label>
        <Textarea data-testid="order-body-input" rows={7} value={f.body} onChange={(e) => set("body", e.target.value)}
          className="rounded-none font-serif text-[15px] leading-relaxed" placeholder="Type the order or award in full…" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label className="label-caps flex items-center gap-2"><FileUp className="h-3.5 w-3.5" /> Signed PDF (optional)</Label>
          <input ref={input} type="file" accept="application/pdf" data-testid="order-signed-file-input"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="block w-full text-sm file:mr-3 file:h-10 file:px-4 file:border-0 file:bg-slate-900 file:text-white file:uppercase file:text-xs file:tracking-widest" />
          <p className="text-[11px] text-muted-foreground">Upload the signed copy now or later from the list below.</p>
        </div>
        <div className={`space-y-2 ${isAward ? "opacity-40 pointer-events-none" : ""}`} data-testid="next-date-block">
          <Label className="label-caps flex items-center gap-2"><CalendarClock className="h-3.5 w-3.5" /> Next date of hearing (optional)</Label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <F label="Date & time" type="datetime-local" value={f.next_at} onChange={(v) => set("next_at", v)} testId="next-date-input" />
            <div className="space-y-2">
              <Label className="label-caps">Hearing type</Label>
              <Select value={f.hearing_type} onValueChange={(v) => set("hearing_type", v)}>
                <SelectTrigger className="rounded-none h-10" data-testid="next-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{HEARING_TYPES.map((h) => <SelectItem key={h.value} value={h.value}>{h.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <F label="Purpose" value={f.purpose} onChange={(v) => set("purpose", v)} testId="next-purpose-input" placeholder="Arguments" />
            <F label="Venue / link" value={f.venue} onChange={(v) => set("venue", v)} testId="next-venue-input" />
          </div>
          {isAward && <p className="text-[11px] text-amber-800">A final award concludes the proceedings — no further date can be fixed.</p>}
        </div>
      </div>
      <Button type="submit" disabled={busy} className={btn} data-testid="submit-order-button">
        <Gavel className="h-3.5 w-3.5 mr-2" /> {busy ? "Recording…" : isAward ? "Pass final award & conclude" : "Record order"}
      </Button>
    </form>
  );
};

const PdfLinks = ({ o, kase, isAssoc, isArb, onReload }) => {
  const can = kase.downloads_allowed;
  const slot = (docId, label, testId, upload) => (
    <div className="flex items-center gap-3" key={testId}>
      <span className="text-[10px] uppercase tracking-wider text-muted-foreground w-20 shrink-0">{label}</span>
      <SignedPdf kind="order" targetId={o.id} documentId={can ? docId : null}
        viewId={isAssoc && kase.my_side_paid ? docId : null} lockedId={!can && !isAssoc ? docId : null}
        canUpload={upload} onDone={onReload} testId={testId} label={`signed ${o.kind.replace(/_/g, " ")}`} />
      {!docId && !upload && <span className="text-[11px] text-muted-foreground">Not on record</span>}
    </div>
  );
  return (
    <div className="mt-3 space-y-1.5">
      {o.document_id && slot(o.document_id, "Generated", `order-pdf-${o.id}`, false)}
      {slot(o.signed_document_id, "Signed", `order-signed-${o.id}`, isArb)}
    </div>
  );
};

export const OrdersProceedings = ({ kase, caseId, orders, procs, isArb, isAdv, isAssoc, onReload, prefill }) => {
  const concluded = ["award_passed", "archived"].includes(kase.status);
  const items = [
    ...orders.map((o) => ({ t: "order", at: o.issued_on || o.created_at, o })),
    ...procs.map((p) => ({ t: "sitting", at: p.scheduled_at, p })),
  ].sort((a, b) => (b.at || "").localeCompare(a.at || ""));

  return (
    <Section title="Orders & proceedings" testId="orders-proceedings-section"
      form={isArb ? (concluded ? (
        <p className="text-sm" data-testid="proceedings-concluded">Proceedings stand concluded — the final award has been passed on {fmtDate(kase.award_passed_at)}.</p>
      ) : <Composer caseId={caseId} onDone={onReload} prefill={prefill} />) : null}>
      {items.length === 0 ? (
        <p className="px-6 py-10 text-sm text-muted-foreground" data-testid="orders-proceedings-empty">No orders passed or sittings posted yet.</p>
      ) : items.map((it) => it.t === "sitting" ? (
        <div key={`p-${it.p.id}`} className="border-b border-border last:border-0 bg-muted/20">
          <ProceedingRow p={it.p} isArb={isArb} isAdv={isAdv} onReload={onReload} canDownload={kase.downloads_allowed} caseId={caseId} />
        </div>
      ) : (
        <div key={`o-${it.o.id}`} className="px-6 py-4 border-b border-border last:border-0" data-testid={`order-row-${it.o.id}`}>
          <div className="flex flex-wrap items-center gap-3">
            <Badge className={KIND_CLASS[it.o.kind] || "bg-slate-100 text-slate-800"}>{it.o.kind.replace(/_/g, " ")}</Badge>
            <p className="text-sm font-medium">{it.o.number ? `${it.o.number} — ` : ""}{it.o.title}</p>
            <span className="text-xs text-muted-foreground ml-auto">{fmtDate(it.o.issued_on || it.o.created_at)}</span>
          </div>
          {it.o.summary && <p className="text-xs text-muted-foreground mt-2 max-w-2xl">{it.o.summary}</p>}
          {it.o.body && (
            <details className="mt-2 max-w-3xl" data-testid={`order-body-${it.o.id}`}>
              <summary className="text-xs text-blue-900 dark:text-blue-400 cursor-pointer">Read the order</summary>
              <p className="mt-2 text-sm whitespace-pre-wrap font-serif leading-relaxed">{it.o.body}</p>
            </details>
          )}
          <PdfLinks o={it.o} kase={kase} isAssoc={isAssoc} isArb={isArb} onReload={onReload} />
        </div>
      ))}
    </Section>
  );
};
